import * as d3 from "d3";
import { useLayoutEffect } from "react";

import { BarSeriesType } from "@/components/organisms/DateTimeline/DateTimeline.types";
import { generateRoundedRectPath } from "@/components/organisms/DateTimeline/DateTimeline.utils";

interface DateTimelineBarSeriesPropsType<T> {
  g: d3.Selection<SVGGElement, unknown, null, undefined>;
  series: BarSeriesType;
  xScale: d3.ScaleBand<Date>;
  yScale: d3.ScaleLinear<number, number>;
  data: T[];
  dateKey: keyof T;
  seriesIndex: number;
  chartSeriesBandwidth: number;
  isInitialized: React.MutableRefObject<boolean>;
  chartHeight: number;
  floatingGap: number;
  style: any;
}

export function DateTimelineBarSeries<T>({
  g,
  series,
  xScale,
  yScale,
  data,
  dateKey,
  seriesIndex,
  chartSeriesBandwidth,
  isInitialized,
  chartHeight,
  floatingGap,
  style,
}: DateTimelineBarSeriesPropsType<T>) {
  useLayoutEffect(() => {
    if (!g) return;

    const finalBarWidth = style?.maxWidth
      ? Math.min(chartSeriesBandwidth, style.maxWidth)
      : chartSeriesBandwidth;

    const stack = d3
      .stack()
      .keys(series.valueKeys)
      .order(d3.stackOrderNone)
      .offset(d3.stackOffsetNone);

    const stackedData = stack(data as any);

    const groupSelection = g
      .selectAll(`.bar-series-${seriesIndex}`)
      .data(stackedData);

    const groupEnter = groupSelection
      .enter()
      .append("g")
      .attr("class", `bar-group bar-series-${seriesIndex}`);

    groupSelection
      .merge(groupEnter)
      .attr("fill", (d, j) => style.colors?.[j] ?? "orange");

    const rectSelection = groupSelection
      .merge(groupEnter)
      .selectAll("path")
      .data((d) => {
        return d.map((point) => ({
          ...point,
          seriesIndex: d.index,
        }));
      });

    const pathEnter = rectSelection.enter().append("path");

    const pathUpdate = rectSelection.merge(pathEnter);

    if (!isInitialized.current) {
      setTimeout(() => {
        pathUpdate
          .attr("d", (d: any) => {
            const x =
              xScale(d.data[dateKey])! +
              seriesIndex * chartSeriesBandwidth +
              (chartSeriesBandwidth - finalBarWidth) / 2;
            const y = chartHeight - floatingGap;
            // Determine if this bar is visually top or bottom based on visible bars
            const visibleStackedData = stackedData.filter((_, idx) => {
              const dataPoint = stackedData[idx].find(
                (point: any) => point.data === d.data,
              );
              return dataPoint && dataPoint[1] - dataPoint[0] !== 0;
            });
            const isTop =
              d.seriesIndex ===
              visibleStackedData[visibleStackedData.length - 1]?.index;
            const isBottom = d.seriesIndex === visibleStackedData[0]?.index;
            return generateRoundedRectPath(
              x,
              y,
              finalBarWidth,
              0,
              style.borderRadius,
              isTop,
              isBottom,
            );
          })
          .transition()
          .duration(750)
          .delay((d: any) => {
            const barIndex = data.findIndex(
              (cd) => cd[dateKey] === d.data[dateKey],
            );
            return barIndex * 15;
          })
          .attr("d", (d: any) => {
            const x =
              xScale(d.data[dateKey])! +
              seriesIndex * chartSeriesBandwidth +
              (chartSeriesBandwidth - finalBarWidth) / 2;
            const y = yScale(d[1]);
            const height = Math.max(0, yScale(d[0]) - yScale(d[1]));
            // Determine if this bar is visually top or bottom based on visible bars for this data point
            const visibleStackedData = stackedData.filter((_, idx) => {
              const dataPoint = stackedData[idx].find(
                (point: any) => point.data === d.data,
              );
              return dataPoint && dataPoint[1] - dataPoint[0] !== 0;
            });
            const isTop =
              d.seriesIndex ===
              visibleStackedData[visibleStackedData.length - 1]?.index;
            const isBottom = d.seriesIndex === visibleStackedData[0]?.index;
            return generateRoundedRectPath(
              x,
              y,
              finalBarWidth,
              height,
              style.borderRadius,
              isTop,
              isBottom,
            );
          });
      }, 0);
    } else {
      pathUpdate.attr("d", (d: any) => {
        const x =
          xScale(d.data[dateKey])! +
          seriesIndex * chartSeriesBandwidth +
          (chartSeriesBandwidth - finalBarWidth) / 2;
        const y = yScale(d[1]);
        const height = Math.max(0, yScale(d[0]) - yScale(d[1]));
        // Determine if this bar is visually top or bottom based on visible bars for this data point
        const visibleStackedData = stackedData.filter((_, idx) => {
          const dataPoint = stackedData[idx].find(
            (point: any) => point.data === d.data,
          );
          return dataPoint && dataPoint[1] - dataPoint[0] !== 0;
        });
        const isTop =
          d.seriesIndex ===
          visibleStackedData[visibleStackedData.length - 1]?.index;
        const isBottom = d.seriesIndex === visibleStackedData[0]?.index;
        return generateRoundedRectPath(
          x,
          y,
          finalBarWidth,
          height,
          style.borderRadius,
          isTop,
          isBottom,
        );
      });
    }
  }, [
    g,
    series,
    xScale,
    yScale,
    data,
    dateKey,
    seriesIndex,
    chartSeriesBandwidth,
    isInitialized,
    chartHeight,
    floatingGap,
    style,
  ]);

  return null;
}
